import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router';
import { Icon, Loading, Toasts } from './components/ui';
import { MatchmakingOverlay } from './components/MatchmakingOverlay';
import { useT } from './i18n';
import { wireGameStore, useGame } from './store/game';
import { realtime, useSession } from './store/session';
import { OFFLINE_TABLE_ID } from './offline/offline-game';
import { applySettings, useSettings } from './store/settings';
import { Welcome } from './screens/Onboarding';
import { Login } from './screens/Login';
import { Home } from './screens/Home';
import { GameTables } from './screens/GameTables';
import { TableScreen } from './games/TableScreen';
import { Replay } from './screens/Replay';
import { Friends } from './screens/Friends';
import { Profile } from './screens/Profile';
import { Settings } from './screens/Settings';
import { Store } from './screens/Store';
import { Shop } from './screens/Shop';
import { Rewards } from './screens/Rewards';
import { Leaderboards } from './screens/Leaderboards';
import { Notifications } from './screens/Notifications';
import { RoomCreate, RoomLobby, JoinLanding } from './screens/Rooms';
import { Tutorial } from './screens/Tutorial';
import { Help } from './screens/Help';
import { Wallet } from './screens/Wallet';
import { Tournaments } from './screens/Tournaments';
import { useSeo } from './lib/seo';
import { initNativeShell, isNative } from './native/platform';
import { registerPush } from './native/push';
import { initAds } from './native/ads';

const Admin = lazy(() => import('./admin/Admin'));

function ConnectionBanner() {
  const ws = useSession((s) => s.ws);
  const status = useSession((s) => s.status);
  const t = useT();
  const local = useGame((s) => s.table?.tableId === OFFLINE_TABLE_ID);
  if (status !== 'authed' || local) return null;
  if (ws === 'offline') return <div className="banner offline" role="status">{t('error.offline')}</div>;
  if (ws === 'reconnecting') return <div className="banner" role="status"><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> {t('error.reconnecting')}</div>;
  return null;
}

function MaintenanceBanner() {
  const boot = useSession((s) => s.boot);
  const t = useT();
  if (!boot?.maintenance) return null;
  return <div className="banner" role="status">{t('error.maintenance')}</div>;
}

function Nav() {
  const t = useT();
  const unread = useSession((s) => s.unread);
  const items: [string, string, string][] = [
    ['/', 'home', t('nav.home')],
    ['/games/CALL_BREAK', 'play', t('nav.play')],
    ['/friends', 'users', t('nav.friends')],
    ['/leaderboards', 'trophy', t('nav.ranks')],
    ['/profile', 'user', t('nav.profile')],
  ];
  return (
    <nav className="bottom-nav" aria-label="Main">
      {items.map(([to, icon, label]) => (
        <NavLink key={to} to={to} end={to === '/'} className={({ isActive }) => (isActive ? 'active' : '')}>
          <Icon name={icon} />
          <span>{label}</span>
          {to === '/' && unread > 0 && <span className="badge">{unread > 9 ? '9+' : unread}</span>}
        </NavLink>
      ))}
    </nav>
  );
}

function Shell({ children, nav = true }: { children: ReactNode; nav?: boolean }) {
  return (
    <div className={`app-shell ${nav ? 'with-nav' : ''}`}>
      {children}
      {nav && <Nav />}
    </div>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const status = useSession((s) => s.status);
  const loc = useLocation();
  if (status === 'loading') return <div className="splash"><Loading /></div>;
  if (status === 'anon') return <Navigate to={`/welcome?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <>{children}</>;
}

/** Sends the player to the table as soon as matchmaking finds one. */
function MatchRouter() {
  const navigate = useNavigate();
  const table = useGame((s) => s.table);
  const mm = useGame((s) => s.mm);
  const loc = useLocation();
  useEffect(() => {
    if (mm?.state !== 'MATCHED' || !mm.tableId) return;
    if (!loc.pathname.startsWith(`/table/${mm.tableId}`)) {
      navigate(`/table/${mm.tableId}`);
      return;
    }
    // Already on that table (e.g. the server put you back at a match you never left): no new snapshot is coming to clear the
    // "Table found!" overlay, so close it here.
    useGame.setState({ mm: null });
  }, [mm, navigate, loc.pathname]);
  // Backstop: never leave "Table found!" covering the screen.
  useEffect(() => {
    if (mm?.state !== 'MATCHED') return;
    const h = setTimeout(() => {
      if (useGame.getState().mm?.state === 'MATCHED') useGame.setState({ mm: null });
    }, 6000);
    return () => clearTimeout(h);
  }, [mm]);
  useEffect(() => {
    if (table && !loc.pathname.startsWith('/table/') && useGame.getState().mm?.state === 'MATCHED') navigate(`/table/${table.tableId}`);
  }, [table, navigate, loc.pathname]);
  return null;
}

function NativeBridge() {
  const navigate = useNavigate();
  const status = useSession((s) => s.status);
  useEffect(() => {
    void initNativeShell(
      (p) => navigate(p),
      (away) => {
        if (away > 5000 && useSession.getState().status === 'authed') realtime.forceReconnect();
      },
    );
  }, [navigate]);
  useEffect(() => {
    if (!isNative || status !== 'authed') return;
    void registerPush((p) => navigate(p));
    void initAds();
  }, [status, navigate]);
  return null;
}

export function App() {
  const init = useSession((s) => s.init);
  const settings = useSettings((s) => s.settings);
  useEffect(() => {
    wireGameStore();
    void init();
  }, [init]);
  useEffect(() => applySettings(settings), [settings]);
  useSeo();

  return (
    <>
      <ConnectionBanner />
      <MaintenanceBanner />
      <Toasts />
      <MatchRouter />
      <NativeBridge />
      <MatchmakingOverlay />
      <Routes>
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/r/:code" element={<Welcome />} />
        <Route path="/login" element={<Login />} />
        <Route path="/join/:code" element={<JoinLanding />} />
        <Route path="/" element={<RequireAuth><Shell><Home /></Shell></RequireAuth>} />
        <Route path="/games/:type" element={<RequireAuth><Shell><GameTables /></Shell></RequireAuth>} />
        <Route path="/table/:tableId" element={<RequireAuth><TableScreen /></RequireAuth>} />
        <Route path="/watch/:tableId" element={<RequireAuth><TableScreen spectate /></RequireAuth>} />
        <Route path="/replay/:matchId" element={<RequireAuth><Replay /></RequireAuth>} />
        <Route path="/friends" element={<RequireAuth><Shell><Friends /></Shell></RequireAuth>} />
        <Route path="/profile" element={<RequireAuth><Shell><Profile /></Shell></RequireAuth>} />
        <Route path="/profile/:id" element={<RequireAuth><Shell><Profile /></Shell></RequireAuth>} />
        <Route path="/settings" element={<RequireAuth><Shell><Settings /></Shell></RequireAuth>} />
        <Route path="/shop" element={<RequireAuth><Shell><Shop /></Shell></RequireAuth>} />
        <Route path="/store" element={<RequireAuth><Shell><Store /></Shell></RequireAuth>} />
        <Route path="/wallet" element={<RequireAuth><Shell><Wallet /></Shell></RequireAuth>} />
        <Route path="/rewards" element={<RequireAuth><Shell><Rewards /></Shell></RequireAuth>} />
        <Route path="/leaderboards" element={<RequireAuth><Shell><Leaderboards /></Shell></RequireAuth>} />
        <Route path="/notifications" element={<RequireAuth><Shell><Notifications /></Shell></RequireAuth>} />
        <Route path="/tournaments" element={<RequireAuth><Shell><Tournaments /></Shell></RequireAuth>} />
        <Route path="/rooms/new" element={<RequireAuth><Shell><RoomCreate /></Shell></RequireAuth>} />
        <Route path="/rooms/:code" element={<RequireAuth><Shell><RoomLobby /></Shell></RequireAuth>} />
        <Route path="/tutorial/:game" element={<Shell nav={false}><Tutorial /></Shell>} />
        <Route path="/help" element={<Shell><Help /></Shell>} />
        <Route path="/admin/*" element={<RequireAuth><Suspense fallback={<Loading />}><Admin /></Suspense></RequireAuth>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
